import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CitationEntry } from '@/lib/types/search-results';
import type { ClaimsResponse } from '@/lib/citations/claims';
import type { CompactStructure } from '@/lib/usrs/structure-parser';

export interface DocumentContent {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  contentType: 'html' | 'text' | 'markdown';
  source: 'pisrs' | 'eurlex' | 'sodnapraksa' | 'usrs' | 'hudoc' | 'other';
  sourceUrl?: string;
  metadata?: {
    pisrsId?: string;
    celex?: string;
    caseId?: string;
    date?: string;
    status?: string;
    relatedLaws?: string[];
    // USRS specific
    caseNumber?: string;
    decisionType?: string;
    /**
     * Strukturna karta USRS odločbe (vizija I.7 »Kje v odločbi«): dokumenti
     * zadeve + sekcijsko drevo z razponi točk in sidri (kompaktna oblika,
     * brez besedil točk). Prisotnost vklopi DecisionMapPanel v viewerju.
     */
    usrsStructure?: CompactStructure;
    /** Doktrina-niti (🧵/1): true = panel citatne verige odprt ob prikazu. */
    doctrineThread?: boolean;
    // EUR-Lex specific
    type?: string; // direktiva, uredba, sklep, sodba
    euArticle?: string; // Article number for ELI scroll-to (e.g., "32", "14a")
    /**
     * KG graf pogled (vizija §I.1): prisotnost pomeni, da viewer namesto
     * besedila renderira KgGraphView z lečami. root = kratica ali mopedID,
     * art = številka člena (leča Sklici).
     */
    kgGraph?: { root: string; lens?: string; art?: string };
    /** Robna številka NS (seja 297) — refetch po reloadu prek /api/robna. */
    robnaSt?: number;
  };
}

// History entry preserves full panel state (mode + document + canvas)
export interface HistoryEntry {
  mode: 'view' | 'canvas' | 'claims';
  document: DocumentContent | null;
  canvasDocId: string | null;
}

interface DocumentViewerState {
  // Panel visibility
  isOpen: boolean;

  // Current document
  document: DocumentContent | null;

  // History of opened documents (back stack) — includes mode + canvas state
  history: HistoryEntry[];

  // Forward stack (for redo navigation)
  forwardStack: HistoryEntry[];

  // Panel size (percentage)
  panelSize: number;

  // Citation highlight state
  highlightSnippet: string | null;
  highlightCitationId: number | null;
  // Kateremu dokumentu highlight pripada (seja 297). Brez tega je »Odpri člen«
  // iz grafa izgubil skok: cleanup ob menjavi dokumenta je počistil highlight,
  // ki je bil postavljen HKRATI z novim dokumentom.
  highlightDocId: string | null;

  // Pending question for chat input (cross-component communication)
  pendingDocumentQuestion: string | null;

  // Panel mode: document view, canvas editor, or claims & evidence
  mode: 'view' | 'canvas' | 'claims';
  activeCanvasDocId: string | null;

  // Claims & Evidence data (set from ChatInterface when claims arrive)
  activeClaims: ClaimsResponse | null;
  activeClaimsCitationMap: CitationEntry[] | null;
  claimsCitationHandler: ((citation: CitationEntry) => void) | null;

  // Pending fragment to insert into canvas (consumed by CanvasEditor)
  pendingFragment: { html: string; sourceType: string; sourceLabel: string } | null;
  /** Čas člena (seja 393): po odprtju predpisa iz kartice/opombe različic odpri okvir različic na tem členu. */
  pendingClenVerzija: { lawId: string; clen: string } | null;

  // Focus mode: when true, AI obravnava vsak query kot follow-up o odprtem dokumentu.
  // Auto-aktivira se ob odpiranju ne-PISRS dokumenta (USRS/EUR-Lex/SP/ESČP/e-KURS) —
  // tipično si pravnik takrat želi pogovor o tem dokumentu. PISRS akti so primarni
  // iskalni cilj (pri njih ostaja iskanje sorodnih aktov/posegov US smiselno), zato off.
  // Resetira se ob zaprtju panela; uporabnik ga lahko kadarkoli izklopi (gumb Reset).
  focusMode: boolean;

  // Actions
  openDocument: (doc: DocumentContent) => void;
  openDocumentWithHighlight: (doc: DocumentContent, snippet: string, citationId: number) => void;
  updateDocumentContent: (partial: Partial<DocumentContent>) => void;
  updateDocumentWithHighlight: (partial: Partial<DocumentContent>, snippet: string, citationId: number) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setPanelSize: (size: number) => void;
  goBack: () => void;
  goForward: () => void;
  /** Skoči na vnos v history (indeks v polju history); seja 343, seznam odprtih dokumentov. */
  jumpToHistory: (index: number) => void;
  /** Skoči na vnos v forwardStack (indeks v polju forwardStack). */
  jumpToForward: (index: number) => void;
  removeHistoryEntry: (index: number) => void;
  removeForwardEntry: (index: number) => void;
  /** Odstrani vnose canvas dokumentov iz zgodovine (ob izbrisu canvas dokumenta). */
  removeCanvasEntries: (canvasDocIds: string[]) => void;
  /** Zapre trenutni dokument: preklopi na naslednjega (naprej, sicer nazaj); brez drugih izprazni pregledovalnik. */
  closeCurrentDocument: () => void;
  /** Zapre vse dokumente in izprazni pregledovalnik (plošča se skrije). */
  closeAllDocuments: () => void;
  clearHistory: () => void;
  clearHighlight: () => void;
  setPendingDocumentQuestion: (q: string | null) => void;
  setMode: (mode: 'view' | 'canvas' | 'claims') => void;
  setActiveCanvasDocId: (id: string | null) => void;
  setClaims: (claims: ClaimsResponse | null, citationMap: CitationEntry[] | null) => void;
  setClaimsCitationHandler: (handler: ((citation: CitationEntry) => void) | null) => void;
  setPendingFragment: (fragment: { html: string; sourceType: string; sourceLabel: string } | null) => void;
  setPendingClenVerzija: (p: { lawId: string; clen: string } | null) => void;
  setFocusMode: (enabled: boolean) => void;
}

// ---- Persist helperji (TODO #15) ----
//
// Strategija: MAJHNE dokumente (USRS odločbe ~15 KB, e-KURS komentarji, NS
// robne, sodna praksa) persistamo Z VSEBINO — zanje zanesljiv re-fetch ni
// vedno mogoč (e-KURS/NS imata source 'other' brez metapodatkov, USRS skupne
// opravilne številke na us-rs.si vrnejo 404). VELIKI dokumenti (PISRS zakoni,
// EUR-Lex konsolidirani akti do 27 MB) se shranijo brez vsebine in se ob
// mountu ponovno naložijo (refetch effect v DocumentViewer).

/** Vsebina do te velikosti se persista; večja se ob mountu ponovno fetcha. */
const PERSIST_CONTENT_MAX = 100_000;
/** Runtime omejitev history (RAM — vnosi nosijo polne vsebine). */
const MAX_HISTORY = 30;
/** Največ toliko zadnjih history vnosov gre v localStorage (najstarejši se
 *  obrežejo; ob kvoti viewerQuotaSafeStorage degradira vsebine → zgodovino). */
const PERSIST_HISTORY_MAX = MAX_HISTORY;

/**
 * Marker za prehodne placeholderje (»Nalagam vsebino…«, fallback sporočila).
 * Vsebina z markerjem se NIKOLI ne persista — po reloadu se tako sproži
 * ponovni fetch namesto zamrznjenega placeholderja.
 */
export const VIEWER_PLACEHOLDER_MARKER = 'data-viewer-placeholder';

function stripDocContent(doc: DocumentContent | null): DocumentContent | null {
  if (!doc) return null;
  if (
    doc.content.length <= PERSIST_CONTENT_MAX &&
    !doc.content.includes(VIEWER_PLACEHOLDER_MARKER)
  ) {
    return doc;
  }
  return { ...doc, content: '' };
}

function stripEntryContent(entry: HistoryEntry): HistoryEntry {
  return { ...entry, document: stripDocContent(entry.document) };
}

function isQuotaExceededError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      e.code === 22 ||
      e.code === 1014)
  );
}

/**
 * Kvota-varna shramba: ob QuotaExceeded najprej odvrže vse vsebine (ostanejo
 * metapodatki → refetch pot), nato še zgodovino. Persist write ne sme nikoli
 * tiho pasti (nauk iz canvas kvota buga, seja 204).
 */
const viewerQuotaSafeStorage = {
  getItem: (name: string) =>
    typeof localStorage !== 'undefined' ? localStorage.getItem(name) : null,
  removeItem: (name: string) => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(name);
  },
  setItem: (name: string, value: string) => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(name, value);
      return;
    } catch (e) {
      if (!isQuotaExceededError(e)) {
        console.warn('[viewer-store] localStorage.setItem napaka (ne-kvota):', e);
        return;
      }
    }
    try {
      const env = JSON.parse(value) as {
        state?: {
          document?: DocumentContent | null;
          history?: HistoryEntry[];
          forwardStack?: HistoryEntry[];
        };
      };
      const s = env.state;
      if (s) {
        if (s.document) s.document = { ...s.document, content: '' };
        s.history = (s.history || []).map((h) => ({
          ...h,
          document: h.document ? { ...h.document, content: '' } : null,
        }));
        s.forwardStack = [];
      }
      try {
        localStorage.setItem(name, JSON.stringify(env));
        console.warn('[viewer-store] kvota presežena — vsebine odvržene, metapodatki ohranjeni');
        return;
      } catch {
        if (s) {
          s.history = [];
          localStorage.setItem(name, JSON.stringify(env));
          console.warn('[viewer-store] kvota presežena — tudi zgodovina odvržena');
        }
      }
    } catch (e2) {
      console.error('[viewer-store] shranjevanje preskočeno (kvota + retry fail):', e2);
    }
  },
};

/**
 * Normalizacija vnosa zgodovine (seja 343). Canvas brez izbranega dokumenta
 * (izbirnik / začetni meni predlog) NI stanje, ki bi ga pravnik hotel obiskati
 * s ← →: če je pod njim odprt dokument, vnos postane navaden pregled tega
 * dokumenta, sicer se zavrže. Pregled brez dokumenta se zavrže. Pravnik je
 * take vnose videl kot »začetni meni, ki se ga ne da izbrisati in se vrti v ciklu«.
 */
export function normalizeHistoryEntry(entry: HistoryEntry): HistoryEntry | null {
  if (entry.mode === 'canvas') {
    if (entry.canvasDocId) return entry;
    return entry.document ? { mode: 'view', document: entry.document, canvasDocId: null } : null;
  }
  return entry.document ? entry : null;
}

/** true = vnos ne nosi ničesar, kar bi bilo vredno vrnitve (glej normalizeHistoryEntry). */
export function isEmptyHistoryEntry(entry: HistoryEntry): boolean {
  return normalizeHistoryEntry(entry) === null;
}

/** Ali dva vnosa kažeta isto stanje (isti dokument v pregledu / isti canvas). */
function sameEntry(a: HistoryEntry, b: HistoryEntry): boolean {
  if (a.mode === 'canvas' || b.mode === 'canvas') {
    return a.mode === b.mode && a.canvasDocId === b.canvasDocId;
  }
  return a.document?.id === b.document?.id;
}

/**
 * Pripne vnos na konec seznama: normalizira ga in preskoči, če je prazen ali
 * enak zadnjemu (npr. pregled B → canvas izbirnik nad B → nazaj bi sicer dal
 * B dvakrat zapored).
 */
function appendEntry(list: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  const norm = normalizeHistoryEntry(entry);
  if (!norm) return list;
  const last = list[list.length - 1];
  if (last && sameEntry(last, norm)) return list;
  return [...list, norm];
}

/** Polja stanja, ki jih nastavi prehod na vnos zgodovine (goBack/goForward/jump). */
function entryToState(entry: HistoryEntry) {
  return {
    document: entry.document,
    mode: entry.mode,
    activeCanvasDocId: entry.canvasDocId,
    highlightSnippet: null,
    highlightCitationId: null,
    highlightDocId: null,
    focusMode: entry.document
      ? entry.document.source !== 'pisrs' && !entry.document.metadata?.kgGraph
      : false,
  };
}

export const useDocumentViewerStore = create<DocumentViewerState>()(
  persist(
    (set, get) => ({
  isOpen: false,
  document: null,
  history: [],
  forwardStack: [],
  panelSize: 30,
  highlightSnippet: null,
  highlightCitationId: null,
  highlightDocId: null,
  pendingDocumentQuestion: null,
  mode: 'view',
  activeCanvasDocId: null,
  activeClaims: null,
  activeClaimsCitationMap: null,
  claimsCitationHandler: null,
  pendingFragment: null,
  pendingClenVerzija: null,
  focusMode: false,

  openDocument: (doc) => {
    const { document: current, mode, activeCanvasDocId, history, forwardStack } = get();
    // If same document is already open, just update metadata (e.g., euArticle for scroll-to)
    if (current && current.id === doc.id && mode === 'view') {
      // isOpen: true je bil tu pozabljen (seja 343): po zaprtju plošče z X in
      // kliku na opombo ISTEGA dokumenta se plošča ni odprla; pravnik je moral
      // na »Odpri dokument«.
      set({ isOpen: true, document: doc, highlightSnippet: null, highlightCitationId: null, highlightDocId: null });
      return;
    }
    // Save current state (including canvas) to history before navigating.
    // TODO #15: forwardStack se NE zavrže (browser semantika je pravniku
    // brisala dokumente — »ostalo samo 5«), ampak linearizira v history:
    // nazaj-veriga po odprtju D je D → F1 → F2 → … → starejši.
    const entry: HistoryEntry = { mode, document: current, canvasDocId: activeCanvasDocId };
    const linearized = appendEntry([...history, ...forwardStack], entry);
    set({
      isOpen: true,
      document: doc,
      history: linearized.slice(-MAX_HISTORY),
      forwardStack: [],
      highlightSnippet: null,
      highlightCitationId: null,
      highlightDocId: null,
      mode: 'view',
      // Graf pogled ni besedilni dokument — fokus način nanj nima smisla
      // (chat o predpisu streže KG kontekst prek kratice, ne prek blob-a).
      focusMode: doc.source !== 'pisrs' && !doc.metadata?.kgGraph,
    });
  },

  openDocumentWithHighlight: (doc, snippet, citationId) => {
    const { document: current, mode, activeCanvasDocId, history, forwardStack } = get();
    const entry: HistoryEntry = { mode, document: current, canvasDocId: activeCanvasDocId };
    const linearized = appendEntry([...history, ...forwardStack], entry);
    set({
      isOpen: true,
      document: doc,
      history: linearized.slice(-MAX_HISTORY),
      forwardStack: [],
      highlightSnippet: snippet,
      highlightCitationId: citationId,
      highlightDocId: doc.id,
      mode: 'view',
      focusMode: doc.source !== 'pisrs',
    });
  },

  updateDocumentContent: (partial) => {
    const current = get().document;
    if (!current) return;
    set({ document: { ...current, ...partial } });
  },

  updateDocumentWithHighlight: (partial, snippet, citationId) => {
    const current = get().document;
    if (!current) return;
    set({
      document: { ...current, ...partial },
      highlightSnippet: snippet,
      highlightCitationId: citationId,
      highlightDocId: partial.id ?? current.id,
    });
  },

  closePanel: () => {
    set({ isOpen: false, focusMode: false });
  },

  togglePanel: () => {
    set({ isOpen: !get().isOpen });
  },

  setPanelSize: (size) => {
    set({ panelSize: size });
  },

  goBack: () => {
    const { history, document, mode, activeCanvasDocId, forwardStack } = get();
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    set({
      ...entryToState(previous),
      history: history.slice(0, -1),
      // Prazno trenutno stanje (izbirnik canvas brez dokumenta) se ne shrani
      forwardStack: appendEntry(forwardStack, currentEntry),
    });
  },

  goForward: () => {
    const { forwardStack, document, mode, activeCanvasDocId, history } = get();
    if (forwardStack.length === 0) return;
    const next = forwardStack[forwardStack.length - 1];
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    set({
      ...entryToState(next),
      forwardStack: forwardStack.slice(0, -1),
      history: appendEntry(history, currentEntry),
    });
  },

  // Linearni vrstni red odprtih dokumentov: history[0..n-1], TRENUTNI,
  // forwardStack[m-1] … forwardStack[0] (vrh sklada = naslednji). Skok na
  // vnos ohrani ta red: vse med ciljem in trenutnim se preloži na drugo stran.
  jumpToHistory: (index) => {
    const { history, forwardStack, document, mode, activeCanvasDocId } = get();
    const target = history[index];
    if (!target) return;
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    const between = history.slice(index + 1).reverse();
    set({
      ...entryToState(target),
      history: history.slice(0, index),
      forwardStack: [...appendEntry(forwardStack, currentEntry), ...between].slice(-MAX_HISTORY),
    });
  },

  jumpToForward: (index) => {
    const { history, forwardStack, document, mode, activeCanvasDocId } = get();
    const target = forwardStack[index];
    if (!target) return;
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    const between = forwardStack.slice(index + 1).reverse();
    set({
      ...entryToState(target),
      forwardStack: forwardStack.slice(0, index),
      history: [...appendEntry(history, currentEntry), ...between].slice(-MAX_HISTORY),
    });
  },

  removeHistoryEntry: (index) => {
    set({ history: get().history.filter((_, i) => i !== index) });
  },

  removeForwardEntry: (index) => {
    set({ forwardStack: get().forwardStack.filter((_, i) => i !== index) });
  },

  removeCanvasEntries: (canvasDocIds) => {
    if (canvasDocIds.length === 0) return;
    const gone = new Set(canvasDocIds);
    // Vnos izbrisanega canvasa: če je pod njim odprt dokument, ostane kot
    // pregled tega dokumenta (appendEntry normalizira in odstrani podvojitve).
    const rebuild = (list: HistoryEntry[]) =>
      list.reduce<HistoryEntry[]>((acc, e) => {
        const hit = e.mode === 'canvas' && e.canvasDocId && gone.has(e.canvasDocId);
        return appendEntry(acc, hit ? { ...e, canvasDocId: null } : e);
      }, []);
    const { history, forwardStack } = get();
    set({ history: rebuild(history), forwardStack: rebuild(forwardStack) });
  },

  closeCurrentDocument: () => {
    const { history, forwardStack } = get();
    if (forwardStack.length > 0) {
      const next = forwardStack[forwardStack.length - 1];
      set({ ...entryToState(next), forwardStack: forwardStack.slice(0, -1) });
      return;
    }
    if (history.length > 0) {
      const previous = history[history.length - 1];
      set({ ...entryToState(previous), history: history.slice(0, -1) });
      return;
    }
    get().closeAllDocuments();
  },

  closeAllDocuments: () => {
    set({
      isOpen: false,
      document: null,
      history: [],
      forwardStack: [],
      mode: 'view',
      activeCanvasDocId: null,
      highlightSnippet: null,
      highlightCitationId: null,
      highlightDocId: null,
      activeClaims: null,
      activeClaimsCitationMap: null,
      focusMode: false,
    });
  },

  clearHistory: () => {
    set({ history: [], forwardStack: [] });
  },

  clearHighlight: () => {
    set({ highlightSnippet: null, highlightCitationId: null, highlightDocId: null });
  },

  setPendingDocumentQuestion: (q) => {
    set({ pendingDocumentQuestion: q });
  },

  setMode: (mode) => {
    set({
      mode,
      // Canvas nima claims — počisti zastarele da se ne prikazujejo napačne
      ...(mode === 'canvas' ? { activeClaims: null, activeClaimsCitationMap: null } : {}),
    });
  },

  setActiveCanvasDocId: (id) => {
    set({ activeCanvasDocId: id });
  },

  setClaims: (claims, citationMap) => {
    set({ activeClaims: claims, activeClaimsCitationMap: citationMap });
  },

  setClaimsCitationHandler: (handler) => {
    set({ claimsCitationHandler: handler });
  },

  setPendingFragment: (fragment) => {
    set({ pendingFragment: fragment });
  },

  setPendingClenVerzija: (p) => {
    set({ pendingClenVerzija: p });
  },

  setFocusMode: (enabled) => {
    set({ focusMode: enabled });
  },
    }),
    {
      name: 'tribonianus-document-viewer',
      storage: createJSONStorage(() => viewerQuotaSafeStorage),
      partialize: (state) => ({
        isOpen: state.isOpen,
        panelSize: state.panelSize,
        mode: state.mode,
        activeCanvasDocId: state.activeCanvasDocId,
        // TODO #15: majhni dokumenti z vsebino, veliki samo metapodatki —
        // zanje DocumentViewer refetch effect vsebino ponovno naloži ob mountu.
        document: stripDocContent(state.document),
        // Tudi history/forwardStack (puščice ← →) preživita reload; goBack na
        // vnos s content === '' sproži isti refetch effect.
        history: state.history.slice(-PERSIST_HISTORY_MAX).map(stripEntryContent),
        forwardStack: state.forwardStack.slice(-PERSIST_HISTORY_MAX).map(stripEntryContent),
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<DocumentViewerState>) };
        // claims podatki niso persistirani — claims mode po reloadu nima vira
        if (merged.mode === 'claims') merged.mode = 'view';
        // Legacy zgodovina (pred sejo 343) je vsebovala prazne canvas vnose
        // (»začetni meni«); počisti jih ob nalaganju.
        merged.history = (merged.history || []).reduce<HistoryEntry[]>(appendEntry, []);
        merged.forwardStack = (merged.forwardStack || []).reduce<HistoryEntry[]>(appendEntry, []);
        // Legacy persisted stanje (pred persistom dokumenta): odprt 'view' brez
        // dokumenta bi pustil prazen pane — raje zapri.
        if (merged.isOpen && merged.mode !== 'canvas' && !merged.document) {
          merged.isOpen = false;
        }
        return merged;
      },
    }
  )
);
